/**
 * CanvasReplayModal — admin wrapper that fetches a submission's stroke data and
 * renders the shared <CanvasReplay> player in a modal.
 *
 * The player itself (SVG path replay, scrubber, speed control) now lives in
 * `@shared-ui/canvas-replay` so the student/teacher attempt-detail page can
 * reuse it. This file keeps only the admin-specific data fetch + modal chrome.
 */

import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../api/adminApi';
import { CanvasReplay } from '@shared-ui/canvas-replay';
import type { StrokeTimelineEvent } from '@shared-ui/canvas-replay';
import '@shared-ui/canvas-replay/CanvasReplay.css';

// Re-export so existing `import { CanvasReplay, StrokeTimelineEvent } from
// '../../components/CanvasReplay'` call sites keep resolving.
export { CanvasReplay } from '@shared-ui/canvas-replay';
export type { StrokeTimelineEvent } from '@shared-ui/canvas-replay';

interface CanvasReplayModalProps {
  submissionId: string;
  onClose: () => void;
}

/**
 * Self-contained modal that fetches stroke data for a submission and renders
 * the CanvasReplay player. Drop into any page — just pass the submissionId.
 */
export function CanvasReplayModal({ submissionId, onClose }: CanvasReplayModalProps) {
  // Resolve canvas data from either the post-cutover blob (canvasJsonUrl, the
  // common case) or the legacy inline fields. The blob bundle is already
  // `{ canvasJSON, strokeTimeline }`, the exact shape CanvasReplay consumes — no
  // transformation. The blob is public (CORS *, GET), so we fetch it directly
  // rather than proxying it through the Function. See canvas-json-blob-storage.md.
  const { data, isLoading, isError } = useQuery({
    queryKey: ['strokeData', submissionId],
    queryFn: async (): Promise<{ canvasJSON: string | null; strokeTimeline: StrokeTimelineEvent[] | null }> => {
      const meta = await adminApi.getSubmissionStrokeData(submissionId);
      if (meta.canvasJsonUrl) {
        const res = await fetch(meta.canvasJsonUrl);
        if (!res.ok) throw new Error(`Canvas blob fetch failed (${res.status})`);
        const bundle = await res.json() as { canvasJSON?: string | null; strokeTimeline?: StrokeTimelineEvent[] | null };
        return { canvasJSON: bundle.canvasJSON ?? null, strokeTimeline: bundle.strokeTimeline ?? null };
      }
      return { canvasJSON: meta.canvasJSON, strokeTimeline: meta.strokeTimeline as StrokeTimelineEvent[] | null };
    },
  });

  return (
    <div className="replay-modal-overlay" onClick={onClose}>
      <div className="replay-modal" onClick={(e) => e.stopPropagation()}>
        <div className="replay-modal-header">
          <h2>Drawing Replay</h2>
          <button className="replay-modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="replay-modal-body">
          {isLoading && <p>Loading stroke data...</p>}
          {isError && <p>Failed to load canvas data for this submission.</p>}
          {data && !isError && !data.canvasJSON && (
            <p>No canvas data available for this submission.</p>
          )}
          {data?.canvasJSON && (
            <CanvasReplay
              canvasJSON={data.canvasJSON}
              strokeTimeline={data.strokeTimeline}
            />
          )}
        </div>
      </div>
    </div>
  );
}
